/* global CVP */

// ## AdobeBaseAuth
//
// Extend this for specific platform functionality.

'use strict';

var App = require('./app');
var _ = require('./vendor/underscore');
var _logger = require('./logger');
var SimpleEmitter = require('./emitter');
var Util = require('./util');
var Dom = require('./dom');
var MVPDConfig = require('./mvpdconfig');


var requestorId = '';
var configString = '';
var authZRequestMap = {
	_requestIds: []
};
var _mvpdId = null;
var _token = null;

var convertXmlStringToXmlDocument = (function () {
	if ('DOMParser' in window) {
		var parser = new window.DOMParser();
		return function (xmlString) {
			var doc;

			doc = parser.parseFromString(xmlString, 'text/xml');

			return doc;
		};
	}
	if ('ActiveXObject' in window) {
		return function (xmlString) {
			var doc;

			doc = new window.ActiveXObject('Microsoft.XMLDOM');
			doc.async = 'false';
			doc.loadXML(xmlString);

			return doc;
		};
	}
}());

var convertAdobeXmlToMvpdConfigObject = (function () {
	var NodeTypes = {
		ELEMENT_NODE:   1,
		ATTRIBUTE_NODE: 2,
		TEXT_NODE:      3
	};

	function addTo(obj, name, value) {
		if (_.isUndefined(obj[name])) {
			obj[name] = value;
		} else {
			if (!_.isArray(obj[name])) {
				obj[name] = [].concat(obj[name]);
			}
			obj[name].push(value);
		}
	}

	function xml2json(xml) {
		if (xml.nodeType === NodeTypes.TEXT_NODE) {
			return xml.nodeValue;
		}
		if (xml.nodeType === NodeTypes.ELEMENT_NODE && xml.hasChildNodes()) {
			var obj = {}, child, children, name, type, i, endi;
			for (i = 0, children = xml.childNodes, endi = children.length; i < endi; i += 1) {
				child = children.item(i);
				type = child.nodeType;
				if (type === NodeTypes.ELEMENT_NODE) {
					name = child.nodeName;
					if (child.hasChildNodes() && child.childNodes.length === 1 && child.firstChild.nodeType === NodeTypes.TEXT_NODE) {
						addTo(obj, name, child.firstChild.nodeValue);
					} else {
						addTo(obj, name, xml2json(child));
					}
				}
			}
			return obj;
		}
	}

	return function (xml) {
		var result;

		if (xml) {
			_logger.log('convertAdobeXmlToMvpdConfigObject: converting Adobe config to mvpd JS object');

			var mvpds = xml.getElementsByTagName('mvpds');

			if (mvpds.length) {
				_logger.log('convertAdobeXmlToMvpdConfigObject: found "mvpds" node(s)');
				result = xml2json(mvpds.item(0));
			}
		}

		return result;
	};
}());

function _byMvpdId(id) {
	return function (mvpd) {
		return mvpd.ID === id;
	};
}

var byMvpdId = _.memoize(_byMvpdId);

var AdobeBaseAuth = CVP.Class.extend({
	init: function (options) {
		_logger.log('AdobeBaseAuth:init', arguments);

		SimpleEmitter.mixin(this);

		_.bindAll(this, '_logout', 'isValidMvpd', 'isVisibleMvpd');

		this.options = options;
		requestorId = options.requestorId;

		// expose globally the methods that the Adobe needs to use
		this.externalizeCallbacks();
	},

	install: function () {
		_logger.log('AdobeBaseAuth:install');

		MVPDConfig.load(this.options);

		this.emit('adobeInitAttempt', {
			adobeVersion  : this.type,
			requestorId   : requestorId,
			adobeUrl      : this.accessEnablerUrl,
			mvpdConfigUrl : MVPDConfig.getMvpdConfigUrl()
		});
	},

	getConfiguredEnvironment: function () {
		return this.configuredEnv;
	},

	getEnv: function () {
		return this.getConfiguredEnvironment();
	},

	getUrlForEnv: function (env, accessEnablerUrl) {
		// Due to Chrome 53 blocking small cross-domain SWFs, we load a local AccessEnablerLoader.swf instead.
		// But if you're using the authVendorEnv query param override, I need to modify the path to the
		// AccessEnablerLoader.swf instead of returning an absolute URL to Adobe's prod/staging AccessEnabler.swf.

		if (accessEnablerUrl) {
			var localAccessEnablerMatch = (/^\/[^/][^?]+/).exec(accessEnablerUrl);

			if (localAccessEnablerMatch) {
				switch (env) {
					case App.VENDOR_ENV_STAGING: return localAccessEnablerMatch[0] + '?staging';
					case App.VENDOR_ENV_PROD: return localAccessEnablerMatch[0];
				}
			}
		}

		switch (env) {
			case App.VENDOR_ENV_STAGING: return this.STAGING_ACCESS_ENABLER_URL;
			case App.VENDOR_ENV_PROD: return this.PRODUCTION_ACCESS_ENABLER_URL;
		}
	},

	getRequestorId: function () {
		return requestorId;
	},


	// MVPD Config methods

	// `getMvpdConfigEntry` is simply a pass-thru to the property's mvpdconfig
	getMvpdConfigEntry: function (id) {
		return MVPDConfig.getMvpdConfigEntry(id);
	},

	isValidMvpd: function (mvpd) {
		return this.PLATFORM_PROPERTY in mvpd && mvpd[this.PLATFORM_PROPERTY] === true;
	},

	isVisibleMvpd: function (mvpd) {
		if ('visible' in mvpd && _.isString(mvpd.visible)) {
			var visible = mvpd.visible;

			if (visible === 'all' || visible === 'true') {
				return true;
			}

			var platforms = visible.split(App.CONFIG_MULTIVALUE_DELIM);

			return _.contains(platforms, this.PLATFORM_PROPERTY);
		}

		return this.isValidMvpd(mvpd);
	},

	getPickerMVPDs: function () {
		return MVPDConfig.getVisibleMVPDs(this.isVisibleMvpd);
	},

	getMVPDs: function () {
		return MVPDConfig.getWhitelistedMVPDs(this.isValidMvpd);
	},

	getVisibleMvpdIds: function () {
		return this._visibleMvpdIds || (this._visibleMvpdIds = _.pluck(this.getPickerMVPDs(), App.MVPD_ID_KEY));
	},

	isVisibleMvpdId: function (id) {
		if (!id) {
			return false;
		}

		return _.contains(this.getVisibleMvpdIds(), id);
	},

	getValidMvpdIds: function () {
		return this._validMvpdIds || (this._validMvpdIds = _.pluck(this.getMVPDs(), App.MVPD_ID_KEY));
	},

	isValidMvpdId: function (id) {
		if (!id) {
			return false;
		}

		return _.contains(this.getValidMvpdIds(), id);
	},

	getMvpdById: function (id) {
		_logger.log('AdobeBaseAuth:getMvpdById', arguments);

		if (!id) {
			return;
		}

		// look up the MVPD in our filtered MVPDs
		var mvpd = _.find(this.getMVPDs(), byMvpdId(id));

		if (_.isUndefined(mvpd)) {
			_logger.warn('AdobeBaseAuth:getMvpdById - MVPD ID "' + id + '" was not found in the MVPD Config.');
		}

		return mvpd;
	},


	// Adobe AccessEnabler methods

	externalizeCallbacks: function () {
		// this takes all the internal methods for adobe callbacks and
		// exposes them to the global scope so adobe can access them
		_logger.log('AdobeBaseAuth:externalizeCallbacks', arguments);

		window.setAuthenticationStatus = _.bind(this._adobeCallback_setAuthenticationStatus, this);
		window.setToken                = _.bind(this._adobeCallback_setToken, this);
		window.tokenRequestFailed      = _.bind(this._adobeCallback_tokenRequestFailed, this);
		window.displayProviderDialog   = _.bind(this._adobeCallback_displayProviderDialog, this);
		window.sendTrackingData        = _.bind(this._adobeCallback_sendTrackingData, this);
		window.createIFrame            = _.bind(this._adobeCallback_createIFrame, this);
		window.destroyIFrame           = _.bind(this._adobeCallback_destroyIFrame, this);
		window.setMovieDimensions      = _.bind(this._adobeCallback_setMovieDimensions, this);
		window.setMetadataStatus       = _.bind(this._adobeCallback_setMetadataStatus, this);
		window.setConfig               = _.bind(this._adobeCallback_setConfig, this);
		window.reportAdobeErrorEvent   = _.bind(this._adobeCallback_reportAdobeErrorEvent, this);
		window.preauthorizedResources  = _.bind(this._adobeCallback_preauthorizedResources, this);
		window.setMvpdRedirectURL      = _.bind(this._adobeCallback_setMvpdRedirectURL, this);
	},

	setRequestor: function (requestorId, serviceEndpoints, options) {
		_logger.log('AdobeBaseAuth:setRequestor', arguments);

		this.emit('setRequestor', {
			requestorId: requestorId
		});

		_logger.info('Adobe:call - setRequestor', requestorId, serviceEndpoints, options);
		this.accessEnabler.setRequestor(requestorId, serviceEndpoints, options);
	},

	checkAuthentication: function () {
		_logger.log('AdobeBaseAuth:checkAuthentication', arguments);

		this.emit('checkAuthentication', {});

		_logger.info('Adobe:call - checkAuthentication');
		this.accessEnabler.checkAuthentication();
	},

	getAuthentication: function (redirectUrl) {
		_logger.log('AdobeBaseAuth:getAuthentication', arguments);

		this.emit('getAuthentication', {
			redirectUrl: redirectUrl
		});

		_logger.info('Adobe:call - getAuthentication', redirectUrl);
		this.accessEnabler.getAuthentication(redirectUrl);
	},

	setAuthenticationStatus: function (isAuthenticated, errorCode) {
		_logger.log('AdobeBaseAuth:setAuthenticationStatus', arguments);

		// get the MVPD that you're authN'ed with
		_logger.log('AdobeBaseAuth:setAuthenticationStatus - getProviderID request');
		this.getProviderID(function getProviderIDResponse(providerId) {
			_logger.log('AdobeBaseAuth:setAuthenticationStatus - getProviderID response');

			// Manipulate data if MVPD is not in whitelist.
			if (isAuthenticated && !providerId) {
				_logger.error('AdobeBaseAuth:setAuthenticationStatus', 'Authenticated with invalid provider.');
				isAuthenticated = false;
				errorCode = 'N201';
			}

			this.emit('setAuthenticationStatus', {
				isAuthenticated: isAuthenticated,
				errorCode: errorCode
			});

			if (isAuthenticated) {
				// update cached mvpd ID
				_mvpdId = providerId;

				// API sugar
				this.emit('authNPass', {
					mvpdId: providerId,
					mvpd: this.getMvpdById(providerId),
					mvpdConfig: this.getMvpdConfigEntry(providerId)
				});
			}
			else {
				// update cached mvpd ID
				_mvpdId = null;

				// update cached token
				_token = null;

				this.emit('authNFail', {
					mvpdId: providerId,
					errorCode: errorCode
				});
			}

		}, this);
	},

	sendTrackingData: function (eventType, trackingData) {
		_logger.log('AdobeBaseAuth:sendTrackingData', arguments);

		// get the MVPD that you're authN'ed with
		_logger.log('AdobeBaseAuth:sendTrackingData - getProviderID request');
		this.getProviderID(function getProviderIDResponse(providerId) {
			_logger.log('AdobeBaseAuth:sendTrackingData - getProviderID response');

			// API sugar
			var data = {};
			switch (eventType) {
				case 'authorizationDetection':
					// Manipulate data if MVPD is not in whitelist.
					if (trackingData[0] && !providerId) {
						_logger.error('AdobeBaseAuth:sendTrackingData', 'Authenticated with invalid provider.');
						trackingData[0] = false;
						trackingData[3] = false;
					}

					data.success = trackingData[0];
					if (data.success) {
						data.mvpdId = trackingData[1];
						data.userId = trackingData[2];
						data.cached = trackingData[3];
					}
					this.emit('authorizationDetection', data);
				break;
				case 'authenticationDetection':
					// Manipulate data if MVPD is not in whitelist.
					if (trackingData[0] && !providerId) {
						_logger.error('AdobeBaseAuth:sendTrackingData', 'Authenticated with invalid provider.');
						trackingData[0] = false;
						trackingData[3] = false;
					}

					data.success = trackingData[0];
					if (data.success) {
						data.mvpdId = trackingData[1];
						data.userId = trackingData[2];
						data.cached = trackingData[3];
					}
					this.emit('authenticationDetection', data);
				break;
				case 'mvpdSelection':
					data.mvpdId = trackingData[0];
					this.emit('mvpdSelection', data);
				break;
			}

			// Emit this only after the manipulation of data is completed above.
			this.emit('sendTrackingData', {
				eventType: eventType,
				trackingData: trackingData
			});

		}, this);
	},

	checkFeature: function (feature) {
		var featureList = this.getFeatureList();

		if (!featureList) {
			return false;
		}

		if (_.isString(feature)) {
			return _.contains(featureList, feature);
		}

		if (_.isArray(feature)) {
			return _.difference(feature, featureList).length === 0;
		}

		return false;
	},

	getFeatureList: function () {
		if (!_mvpdId) {
			_logger.warn('AuthManager:getFeatureList', 'Not authenticated with a provider.');
			return null;
		}

		var mvpd = this.getMvpdById(_mvpdId);

		if (!mvpd) {
			_logger.error('AuthManager:getFeatureList', 'Failed to map MVPD ID "' + _mvpdId + '" to an MVPD object!');
			this.emit('errorEvent', {
				source: 'cvp',
				data: {
					errorId: 'CFG201',
					level: 'error',
					message: 'Attempted to use a provider ("' + _mvpdId + '") that was not found in MVPD config.'
				}
			});
			return null;
		}

		var list = null;
		var string = '';
		var tokenRegex = /\w+/;
		var delimiterRegex = /\s*,\s*/;

		if ('featureList' in mvpd && 'text' in mvpd.featureList) {
			string = mvpd.featureList.text;
			if (tokenRegex.test(string)) {
				list = _.compact(string.split(delimiterRegex));
			}
		}

		return list;
	},

	checkPreauthorizedResources: function (resources) {
		_logger.log('AdobeBaseAuth:checkPreauthorizedResources', arguments);

		this.accessEnabler.checkPreauthorizedResources(resources);
	},

	checkAuthorization: function (resourceId, feature) {
		_logger.log('AdobeBaseAuth:checkAuthorization', arguments);

		// this will return a token or a failure but will never prompt login
		_logger.log('AdobeBaseAuth:checkAuthorization - getProviderID request');
		this.getProviderID(function getProviderIDResponse(providerId) {
			_logger.log('AdobeBaseAuth:checkAuthorization - getProviderID response');

			if (!providerId) {
				var errorCode = 'Z201';
				var errorString = 'Attempted to authorize with a provider that was not found in MVPD config.';

				// Manually throw flow failure event.
				var data = {
					mvpdId: providerId,
					resource: resourceId,
					errorCode: errorCode,
					errorString: errorString
				};

				this.emit('tokenRequestFailed', data);
				this.emit('authZFail', data);

				// Manually throw error event.
				this.emit('errorEvent', {
					source: 'cvp',
					data: {
						errorId: errorCode,
						level: 'error',
						message: errorString
					}
				});

				return;
			}

			this.pushAuthZRequestMapEntry(resourceId, {
				feature: feature,
				mvpdId: providerId
			});

			this.emit('checkAuthorization', {
				mvpdId: providerId,
				resource: resourceId,
				feature: feature
			});

			_logger.info('Adobe:call - checkAuthorization', resourceId);
			this.accessEnabler.checkAuthorization(resourceId);
		}, this);
	},

	getAuthZRequestMap: function () {
		return _.extend({}, authZRequestMap);
	},

	pushAuthZRequestMapEntry: function (resourceId, entry) {
		_logger.log('AdobeBaseAuth:pushAuthZRequestMapEntry', arguments);

		// note what resource IDs have been requested
		authZRequestMap._requestIds.push(resourceId);

		var slot = authZRequestMap[resourceId] = authZRequestMap[resourceId] || [];
		slot.push(entry);
	},

	pullAuthZRequestMapEntry: function (resourceId) {
		_logger.log('AdobeBaseAuth:pullAuthZRequestMapEntry', arguments);

		if (!(resourceId in authZRequestMap)) {
			_logger.error('AdobeBaseAuth:pullAuthZRequestMapEntry', 'failed to locate resourceId "' + resourceId + '"');
			return;
		}

		// remove resource ID from list of IDs upon response
		var index = _.indexOf(authZRequestMap._requestIds, resourceId);
		if (index === -1) {
			_logger.error('AdobeBaseAuth:pullAuthZRequestMapEntry', 'resourceId "' + resourceId + '" is not in list of requested IDs');
		}
		else {
			authZRequestMap._requestIds.splice(index, 1);
		}

		var slot = authZRequestMap[resourceId];
		return slot.shift();
	},

	getOldestAuthZRequestId: function () {
		_logger.log('AdobeBaseAuth:getOldestAuthZRequestId');

		return authZRequestMap._requestIds.length && authZRequestMap._requestIds[0] || null;
	},

	setAccessToken: function (tokenString, feature) {
		_logger.log('AdobeBaseAuth:setAccessToken');

		if (arguments.length === 0) {
			_token = null;
			return;
		}

		var tokenObj = {};
		var tokenParams = {};

		tokenObj.accessTokenType = 'Adobe';
		tokenObj.accessToken = tokenString;
		tokenObj.mvpd = _mvpdId;

		if (feature) {
			var mvpd = this.getMvpdById(_mvpdId);

			if (!mvpd) {
				this.emit('errorEvent', {
					source: 'cvp',
					data: {
						errorId: 'T201',
						level: 'error',
						message: 'Attempted to set token for a provider ("' + _mvpdId + '") that was not found in MVPD config.'
					}
				});
			}
			else if ('featureList' in mvpd) {
				tokenParams.fname = feature;

				if ('text' in mvpd.featureList) {
					tokenParams.flist = mvpd.featureList.text;
				}

				if ('signature' in mvpd.featureList) {
					tokenParams.fsig = mvpd.featureList.signature;
				}

				if ('metadata' in mvpd) {
					tokenParams.fmetadata = mvpd.metadata;
				}

				if (!_.isEmpty(tokenParams)) {
					tokenObj.tokenParams = tokenParams;
				}

			}
		}

		_token = tokenObj;
	},

	getAccessToken: function () {
		_logger.log('AdobeBaseAuth:getAccessToken', _token);

		return _token;
	},

	// `setToken(resource, token)`
	//
	// Intercept response and verify against feature and mvpd.
	//
	setToken: function (resource, token) {
		_logger.log('AdobeBaseAuth:setToken', arguments);

		// HTML5 AccessEnabler providing an array for `resource` param
		if (_.isArray(resource)) {
			resource = resource[0];
		}

		if (resource) {

			var entry = this.pullAuthZRequestMapEntry(resource);

			if (!entry) {
				_logger.error('AdobeBaseAuth:setToken', 'No authZ map entry found for resource ID "' + resource + '"');
				return;
			}

			if (entry.mvpdId !== _mvpdId) {
				_logger.error('AdobeBaseAuth:setToken', 'The authZ map entry for the token response is not for the currently authenticated MVPD');
				return;
			}

			this.setAccessToken(token, entry.feature);

		}

		this.getProviderID(function getProviderIDResponse(providerId) {

			var data = {
				mvpdId: providerId,
				resource: resource,
				token: token
			};

			// authz success
			this.emit('setToken', data);

			// API sugar
			this.emit('authZPass', data);

		}, this);
	},

	// `tokenRequestFailed(resource, errorCode, errorString)`
	//
	// This is to allow manual firing of authZ failure.
	//
	tokenRequestFailed: function (resource, errorCode, errorString) {
		_logger.log('AdobeBaseAuth:tokenRequestFailed', arguments);

		// clear our cache if things didn't work out
		this.setAccessToken();

		if (resource) {

			var entry = this.pullAuthZRequestMapEntry(resource);

			if (!entry) {
				_logger.error('AdobeBaseAuth:tokenRequestFailed', 'No authZ map entry found for resource ID "' + resource + '"');
				return;
			}

		}

		this.getProviderID(function getProviderIDResponse(providerId) {

			var data = {
				mvpdId: providerId,
				resource: resource,
				errorCode: errorCode,
				errorString: errorString
			};

			// authz failure
			this.emit('tokenRequestFailed', data);

			// API sugar
			this.emit('authZFail', data);

		}, this);
	},

	_logout: function () {
		_logger.info('Adobe:call - logout');

		this.emit('logout', {});

		// logs the user out, adobe refreshes page
		this.accessEnabler.logout();
	},

	logout: function () {
		_logger.log('AdobeBaseAuth:logout');

		// defer until other things have a chance to complete
		window.setTimeout(this._logout, 60);
	},

	setProviderDialogURL: function (dialogUrl) {
		_logger.log('AdobeBaseAuth:setProviderDialogURL', arguments);

		this.emit('setProviderDialogURL', {
			url: dialogUrl
		});

		_logger.info('Adobe:call - setProviderDialogURL', dialogUrl);
		this.accessEnabler.setProviderDialogURL(dialogUrl);
	},

	setSelectedProvider: function (id) {
		_logger.log('AdobeBaseAuth:setSelectedProvider', arguments);

		if (id === null) {
			// short-circuit if you're un-selecting MVPD
			_logger.info('Adobe:call - setSelectedProvider', 'nulling out selected provider');
			this.accessEnabler.setSelectedProvider(null);
			return;
		}

		if (!this.isValidMvpdId(id)) {
			_logger.warn('AdobeBaseAuth:setSelectedProvider - MVPD id "' + id + '" was not allowed!');

			this.emit('errorEvent', {
				source: 'cvp',
				data: {
					errorId: 'N201',
					level: 'error',
					message: 'Attempted to authenticate with a provider that was not found in MVPD config.',
					mvpdId: id
				}
			});

			return;
		}

		this.emit('setSelectedProvider', {
			id: id
		});

		_logger.info('Adobe:call - setSelectedProvider', id);
		this.accessEnabler.setSelectedProvider(id);
	},

	getMetadata: function (key /*, args...*/) {
		_logger.log('AdobeBaseAuth:getMetadata', arguments);

		_logger.info('Adobe:call - getMetadata', key);
		this.accessEnabler.getMetadata(key, [].slice.call(arguments, 1));
	},

	checkRegistrationCode: function () {
		throw new Error('AdobeBaseAuth::checkRegistrationCode requires implementation');
	},


	getSelectedProvider: function () {
		throw new Error('AdobeBaseAuth::getSelectedProvider requires implementation');
	},

	_selectedProviderCachedMvpdId: null,
	// expecting { MVPD: mvpdId, AE_State: "New User", "User Not Authenticated", or "User Authenticated" }
	_validateSelectedProvider: function (res) {
		// try to double-check the data we receive to determine its validity
		if (!_.isObject(res) || !_.isString(res.AE_State) || !( _.isString(res.MVPD) || _.isNull(res.MVPD) )) {
			_logger.error('AdobeBaseAuth: "selectedProvider" did not send a valid response:', res);
			throw new Error('getSelectedProvider did not provide a valid response!');
		}

		var mvpdId = res.MVPD;

		// filter out based on presence in mvpdconfig
		if (mvpdId && !this.isValidMvpdId(mvpdId))
		{
			_logger.debug('AdobeBaseAuth: "selectedProvider" response MVPD ("' + mvpdId + '") was not found in MVPDConfig.');

			this.emit('invalidProvider', {
				id: mvpdId
			});

			res.MVPD = mvpdId = null;
		}

		if (this._selectedProviderCachedMvpdId !== mvpdId) {
			_logger.log('AdobeBaseAuth: "selectedProvider" was previously "' + this._selectedProviderCachedMvpdId + '" and is now "' + mvpdId + '".');
		}

		// cache response for debugging
		this._selectedProviderCachedMvpdId = mvpdId;

		return res;
	},


	// usage: getProviderID(function (providerId) { alert(providerId); });
	getProviderID: function (callback, context) {
		_logger.log('AdobeBaseAuth:getProviderID');

		if (!_.isFunction(callback)) {
			_logger.error('getProviderID requires an async callback');
			throw new Error('AdobeBaseAuth::getProviderID requires an async callback');
		}

		_logger.log('AdobeBaseAuth:getProviderID - getSelectedProvider request');

		this.getSelectedProvider(function getSelectedProviderResponse(data) {
			_logger.log('AdobeBaseAuth:getProviderID - getSelectedProvider response', arguments);

			callback.call(context, data.MVPD);
		});
	},


	_adobeLoaded: function () {
		_logger.log('AdobeBaseAuth:_adobeLoaded');

		this.accessEnabler.bind('errorEvent', 'reportAdobeErrorEvent');

		var configInfo = {
			callSetConfig: true,
			backgroundLogin: this.options.refreshlessLogin,
			backgroundLogout: this.options.refreshlessLogout,
			handleRedirect: this.options.handleRedirect && this.type === 'flash' && this.options.refreshlessLogin
		};

		this.setRequestor(this.options.requestorId, null, configInfo);
	},

	_augmentErrorData: function (err) {
		var errorIdRegex = /^([A-Z]+)([0-9]+)$/;

		function isChrome() {
			return CVP.Browser.isBrowser(CVP.Browser.CHROME);
		}

		function hasUnsandboxedLimitations() {
			var flashVersion = CVP.swfobject.getFlashPlayerVersion();
			return isChrome() && flashVersion.major === 11 && flashVersion.minor === 6;
		}

		if ('errorId' in err) {
			var errorId = err.errorId;
			var errorString = err.message;
			var errorHtml = errorString;
			if (errorString) {
				var decoded = Dom.decodeHtml(errorString);
				errorString = decoded.text;
				errorHtml = errorString ? decoded.html : errorString;
				err.message = errorString;
				err.messageHtml = errorHtml;
			}
			var errorMatch = errorIdRegex.exec(errorId);
			if (errorMatch) {
				err.errorCode = errorId;
				var errorGroup = errorMatch[1];
				var errorNumber = errorMatch[2];
				switch (errorGroup) {
					case 'SEC':
						switch (errorNumber) {
							case '403':
								err.errorCategory = App.ErrorCategory.BLOCKED;
								err.devAction = 'Domain Security error. The requestor is using an invalid domain. All domains used by a particular Requestor ID need to be whitelisted by Adobe.';
							break;
							case '412':
								if (this.type === 'html5') {
									err.errorCode += 'a';
								}
								else if (this.type === 'flash') {
									err.errorCode += 'b';
								}
								err.errorCategory = App.ErrorCategory.USER;
								err.resolution = App.Resolution.LOG_IN_AGAIN;
							break;
							case '420':
								err.errorCategory = App.ErrorCategory.BLOCKED;
								err.devAction = 'Load [https://]{SP_FQDN} in browser and manually accept the ssl certificates.  Mark proxy certs as trusted.';
								err.userAction = 'Invalid server certificate.  The authenticity of the host cannot be established.  Possible man-in-the-middle attack!';
							break;
							default:
								_logger.debug('Unknown error code ' + errorId);
						}
					break;
					case 'CFG':
						// CFG5xx error
						if (errorNumber.charAt(0) === '5') {
							err.errorCategory = App.ErrorCategory.BLOCKED;
							err.resolution = App.Resolution.TRY_AGAIN_LATER;
						}
						else {
							switch (errorNumber) {
								case '010':
									err.errorCategory = App.ErrorCategory.BLOCKED;
									err.devAction = 'The setRequestor response is zipped.';
								break;
								case '100':
									err.errorCategory = App.ErrorCategory.WARN;
									err.resolution = App.Resolution.FIX_TIME;
								break;
								case '400':
									err.errorCategory = App.ErrorCategory.BLOCKED;
									err.devAction = 'A valid requestor ID must be specified!';
								break;
								case '404':
									err.errorCategory = App.ErrorCategory.USER;
									err.resolution = App.Resolution.CHECK_NETWORK;
								break;
								case '410':
									err.errorCategory = App.ErrorCategory.USER;
									err.resolution = App.Resolution.CLEAR_CACHE;
								break;
								default:
									_logger.debug('Unknown error code ' + errorId);
							}
						}
					break;
					case 'DRM':
						// default errorCategory for all DRM*** errors
						err.errorCategory = App.ErrorCategory.USER;

						// default resolution for all DRM*** errors
						err.resolution = App.Resolution.RESET_DRM;

						if ('resetDRM' in err && err.resetDRM === true) {
							// auto-recovery by resetting DRM licenses requires a reload of AccessEnabler
							err.resolution = App.Resolution.RELOAD_PAGE;
						}
						else {
							switch (errorNumber) {
								case '3301':
									/* default errorCategory & resolution */
								break;
								case '3305':
									err.resolution = App.Resolution.CHECK_NETWORK;
								break;
								case '3307':
									if (hasUnsandboxedLimitations()) {
										err.errorCode += 'a';
										err.resolution = App.Resolution.ENABLE_UNSANDBOX;
									}
									else {
										err.errorCode += 'b';
										err.errorCategory = App.ErrorCategory.BLOCKED;
									}
								break;
								case '3313':
									err.resolution = App.Resolution.ALLOW_WRITE;
								break;
								case '3315':
									err.resolution = App.Resolution.CHECK_NETWORK;
								break;
								case '3319':
									err.resolution = App.Resolution.RELOAD_PAGE;
								break;
								case '3320':
									/* defaults */
								break;
								case '3321':
									if (isChrome()) {
										err.errorCode += 'a';
										err.resolution = App.Resolution.ENABLE_UNSANDBOX;
									}
									else {
										err.errorCode += 'b';
										err.errorCategory = App.ErrorCategory.BLOCKED;
										err.resolution = App.Resolution.TRY_AGAIN_LATER;
									}
								break;
								case '3322':
									/* assuming 3322a is caught by resetDRM flag */
									err.errorCode += 'b';
								break;
								case '3323':
									/* defaults */
								break;
								case '3325':
									/* defaults */
								break;
								case '3328':
									err.resolution = App.Resolution.TRY_AGAIN_LATER;
								break;
								case '3333':
									err.resolution = App.Resolution.TRY_AGAIN_LATER;
								break;
								case '3346':
									/* assuming 3346a is caught by resetDRM flag */
									err.errorCode += 'b';
								break;
								case '3362':
									err.resolution = App.Resolution.TRY_AGAIN_LATER;
								break;
								case '3364':
									err.resolution = App.Resolution.ALLOW_IDENTIFIERS;
								break;
								case '3365':
									err.resolution = App.Resolution.CEASE_INCOGNITO;
								break;
								case '3368':
									err.resolution = App.Resolution.ENABLE_UNSANDBOX;
								break;
								default:
									_logger.debug('Unknown error code ' + errorId);
									err.resolution = App.Resolution.DRM_PROBLEM;
							}
						}
					break;
					case 'N':
						switch (errorNumber) {
							case '000':
								err.errorCategory = App.ErrorCategory.LOG;
							break;
							case '001':
								err.errorCategory = App.ErrorCategory.INFO;
							break;
							case '005':
								err.errorCategory = App.ErrorCategory.LOG;
							break;
							case '010':
								err.errorCategory = App.ErrorCategory.LOG;
								err.devAction = 'User authenticated while degradation rule was in place.  Optionally inform the user that she is getting "complimentary" free access.';
							break;
							case '011':
								err.errorCategory = App.ErrorCategory.LOG;
								err.devAction = 'User was authenticated using TempPass. Inform the user; optionally present list of regular MVPDs.';
							break;
							case '100':
								err.errorCategory = App.ErrorCategory.LOG;
								err.devAction = 'Logout.';
							break;
							case '110':
								err.errorCategory = App.ErrorCategory.LOG;
								err.devAction = 'Logout.';
							break;
							case '111':
								err.errorCategory = App.ErrorCategory.INFO;
								err.devAction = 'Expired TempPass.  Inform user; present list of regular MVPDs; hide TempPass option.';
							break;
							case '120':
								err.errorCategory = App.ErrorCategory.USER;
								err.devAction = 'Multiple authentication attempts detected!  Are there multiple calls to getAuthentication or setSelectedProvider?';
								err.resolution = App.Resolution.RELOAD_PAGE;
							break;
							case '130':
								// TODO - break down into 'a', 'b' and 'c' suberrors -- Is that possible?  Or just consider N130 as BLOCKED?
								err.errorCategory = App.ErrorCategory.BLOCKED;
								err.devAction = 'User has disabled storage.';
								err.resolution = App.Resolution.ALLOW_WRITE;
							break;
							case '500':
								err.errorCategory = App.ErrorCategory.USER;
								err.devAction = 'Internal error.  Use AccessEnablerDebug and inspect console logs.';
								err.resolution = App.Resolution.TRY_AGAIN_LATER;
							break;
							default:
								_logger.debug('Unknown error code ' + errorId);
						}
					break;
					case 'Z':
						switch (errorNumber) {
							case '010':
								err.errorCategory = App.ErrorCategory.LOG;
								err.devAction = 'User was authorized while degradation rule was in place.';
							break;
							case '011':
								err.errorCategory = App.ErrorCategory.LOG;
								err.devAction = 'User was authorized using TempPass.  Optionally inform user.';
							break;
							case '100':
								err.errorCategory = App.ErrorCategory.USER;
								err.message = err.message || 'You are not authorized to view this content. Please contact your TV provider for further assistance.';
								err.messageHtml = err.messageHtml || err.message;
								err.resolution = App.Resolution.CONTENT_DISALLOWED;
							break;
							case '110':
								err.errorCategory = App.ErrorCategory.USER;
								err.messageHtml = err.message = 'There was a problem verifying your account with your TV provider. Please try again.';
								err.resolution = App.Resolution.POSSIBLE_FRAUD;
							break;
							case '113':
								err.errorCategory = App.ErrorCategory.USER;
								err.message = err.message || 'You are not authorized to view this content. Please contact your TV provider for further assistance.';
								err.messageHtml = err.messageHtml || err.message;
								err.resolution = App.Resolution.MVPD_NOT_AVAILABLE;
							break;
							case '120':
								err.errorCategory = App.ErrorCategory.USER;
								err.messageHtml = err.message = 'There was a problem verifying your account with your TV provider. Please try again.';
								err.resolution = App.Resolution.TRY_AGAIN_LATER;
							break;
							case '130':
								err.errorCategory = App.ErrorCategory.BLOCKED;
								err.messageHtml = err.message = 'There was a problem verifying your account with your TV provider. Please try again or contact your TV provider for further assistance.';
								err.resolution = App.Resolution.CONTENT_DISALLOWED;
							break;
							case '500':
								err.errorCategory = App.ErrorCategory.BLOCKED;
								err.messageHtml = err.message = 'We are experiencing technical difficulties. Please try again or contact your TV provider for further assistance.';
								err.resolution = App.Resolution.TRY_AGAIN_LATER;
							break;
							default:
								_logger.debug('Unknown error code ' + errorId);
						}
					break;
					case 'P':
						switch (errorNumber) {
							case '100':
								err.errorCategory = App.ErrorCategory.WARN;
								err.devAction = 'Preauthorization failed. Most likely this is due to requesting authorization for too many resources.';
							break;
							default:
								_logger.debug('Unknown error code ' + errorId);
						}
					break;
					case 'LS':
						switch (errorNumber) {
							case '010':
								err.errorCategory = App.ErrorCategory.WARN;
								err.resolution = App.Resolution.LSO_STORAGE;
							break;
							case '011':
								err.errorCategory = App.ErrorCategory.WARN;
								err.resolution = App.Resolution.LSO_WRITE;
							break;
							default:
								_logger.debug('Unknown error code ' + errorId);
						}
					break;
				}
			}
		}
	},

	_adobeCallback_reportAdobeErrorEvent: function (errorData) {
		if (!_.isObject(errorData)) {
			_logger.warn('Adobe:callback - reportAdobeErrorEvent', 'Cannot process errorData');
			return;
		}

		_logger.info('Adobe:callback - reportAdobeErrorEvent', errorData.errorId, errorData);

		this.emit('adobeError', errorData);

		this._augmentErrorData(errorData);

		this.emit('errorEvent', {
			source: 'adobe',
			data: errorData
		});
	},

	// get MVPD configs with iframe data from Adobe
	// also set ready based on this callback
	_adobeCallback_setConfig: function (config) {
		_logger.info('Adobe:callback - setConfig', (typeof config === 'string' ? 'received config' : 'did not receive config'));

		var mvpds;

		if (_.isString(config)) {

			// set global-ish variable in case we need it later
			configString = config;

			var adobeConfig;

			try {
				adobeConfig = convertAdobeXmlToMvpdConfigObject(convertXmlStringToXmlDocument(configString));
			} catch (e) {
				_logger.error('AdobeBaseAuth: failed to convert response from setConfig successfully from string to XML to JS object');
			}

			if (!_.isEmpty(adobeConfig) && 'mvpd' in adobeConfig) {
				mvpds = _.isArray(adobeConfig.mvpd) ? adobeConfig.mvpd : [adobeConfig.mvpd];
			}
		}

		if (mvpds) {
			MVPDConfig.updateVendorConfig(mvpds);
			this.emit('adobeInitSuccess', {
				requestorId : this.options.requestorId,
				adobeUrl : this.accessEnablerUrl
			});
		}
		else {
			this.emit('adobeInitFailure', {
				errorCode: 'CFG001',
				errorId: 'CFG001',
				errorString: 'setConfig response was invalid',
				errorCategory: App.ErrorCategory.BLOCKED,
				resolution: App.Resolution.TRY_AGAIN_LATER
			});
		}

		if (this.options.customPicker) {
			// disable AE's Flash picker
			this.setProviderDialogURL('none');
		}

		this._onMvpdConfigSuccess = _.bind(this._onMvpdConfigSuccess, this, {
			adobeVersion        : this.type,
			adobeStaging        : this.getEnv() === App.VENDOR_ENV_STAGING,
			adobeSwfUrl         : this.options.adobeSwfUrl,
			adobeJsUrl          : this.options.adobeJsUrl,
			requestorId         : this.options.requestorId,
			customMVPDPicker    : this.options.customPicker,
			mvpdConfigUrl       : MVPDConfig.getMvpdConfigUrl(),
			accessEnablerId     : this.accessEnablerId,
			accessEnablerUrl    : this.accessEnablerUrl
		});

		MVPDConfig.ready.then(this._onMvpdConfigSuccess, this._onMvpdConfigFailure);
	},

	_onMvpdConfigSuccess: function (vendorData) {
		_logger.log('MVPDConfig success allowed AccessEnabler to be ready!');
		this.emit('ready', vendorData);
	},

	_onMvpdConfigFailure: function () {
		_logger.error('MVPDConfig failure prohibited AccessEnabler from being ready!');
	},

	getConfigString: function () {
		return configString;
	},

	_adobeCallback_setMvpdRedirectURL: function (data) {
		_logger.info('Adobe:callback - setMvpdRedirectURL', arguments);

		this.emit('setMvpdRedirectURL', data);
	},

	_adobeCallback_preauthorizedResources: function (resources) {
		_logger.info('Adobe:callback - preauthorizedResources', arguments);

		this.emit('preauthorizedResources', {
			resources: resources
		});
	},

	_adobeCallback_setToken: function (resource, token) {
		_logger.info('Adobe:callback - setToken', arguments);

		this.setToken(resource, token);
	},

	_adobeCallback_tokenRequestFailed: function (resource, errorCode, errorString) {
		_logger.info('Adobe:callback - tokenRequestFailed', arguments);

		this.tokenRequestFailed(resource, errorCode, errorString);
	},

	_adobeCallback_setAuthenticationStatus: function (isAuthenticated, errorCode) {
		_logger.info('Adobe:callback - setAuthenticationStatus', arguments);

		this.setAuthenticationStatus(Number(isAuthenticated), errorCode);
	},

	_adobeCallback_sendTrackingData: function (eventType, trackingData) {
		var key, value;
		_logger.info('Adobe:callback - sendTrackingData', arguments);

		// HACK: convert "true"/"false" into Boolean; discrepancy between Flash and HTML5 AccessEnabler solutions
		for (key in trackingData) {
			if (_.has(trackingData, key)) {
				value = trackingData[key];
				if (value === 'true') {
					trackingData[key] = true;
				}
				if (value === 'false') {
					trackingData[key] = false;
				}
			}
		}

		this.sendTrackingData(eventType, trackingData);
	},

	_adobeCallback_setMetadataStatus: function (key, args, value) {
		_logger.info('Adobe:callback - setMetadataStatus', arguments);

		this.emit('setMetadataStatus', {
			key: key,
			args: args,
			value: value
		});
	},

	_adobeCallback_displayProviderDialog: function (/*providers*/) {
		_logger.info('Adobe:callback - displayProviderDialog', arguments);

		this.displayProviderDialog();
	},

	displayProviderDialog: function () {
		this.emit('displayProviderDialog', {
			mvpds: this.getPickerMVPDs()
		});
	},

	_adobeCallback_createIFrame: function (w, h) {
		_logger.info('Adobe:callback - createIFrame', arguments);

		// turn params to numbers if they aren't already
		var width = Util.toInt(w);
		var height = Util.toInt(h);

		this.emit('createIFrame', {
			width: width,
			height: height
		});
	},

	_adobeCallback_destroyIFrame: function () {
		_logger.info('Adobe:callback - destroyIFrame', arguments);

		this.emit('destroyIFrame', {});
	},

	_adobeCallback_setMovieDimensions: function (w, h) {
		_logger.info('Adobe:callback - setMovieDimensions', arguments);
		// this sets the movie dimensions for the swf,
		// usually called when not doing a custom picker

		// turn params to numbers if they aren't already
		var width = Util.toInt(w);
		var height = Util.toInt(h);

		this.emit('setMovieDimensions', {
			width: width,
			height: height
		});
	}
});


module.exports = AdobeBaseAuth;
